import { Link } from "react-router-dom";
import {
  ArrowRight, Boxes, CalendarClock, CreditCard, Megaphone, MessageCircle,
} from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { useWhatsappConfig } from "@/features/whatsapp/use-whatsapp.js";

/**
 * Central de integrações. O cartão do WhatsApp mostra o estado real da
 * conexão — no app antigo o selo "Disponível" era fixo, independente de a
 * integração estar configurada ou não.
 */
export function IntegrationsPage() {
  const { data: whatsapp } = useWhatsappConfig();

  const integracoes = [
    {
      title: "WhatsApp Meta API",
      description: "Conecte o número oficial da clínica via WhatsApp Cloud API (Meta).",
      icon: MessageCircle,
      to: "/integracoes/whatsapp-meta",
      status: whatsapp?.connected ? "conectado" : whatsapp?.configured ? "pendente" : "disponivel",
      tone: "from-[#25D366]/20 to-[#128C7E]/20 text-[#128C7E]",
    },
    {
      title: "Meta Ads",
      description: "Importe campanhas e leads do Facebook e Instagram.",
      icon: Megaphone,
      status: "breve",
      tone: "from-primary/15 to-chart-2/15 text-primary",
    },
    {
      title: "Pagamentos",
      description: "Receba pagamentos e gere cobranças automáticas.",
      icon: CreditCard,
      status: "breve",
      tone: "from-chart-3/15 to-chart-4/15 text-chart-3",
    },
    {
      title: "Google Agenda",
      description: "Sincronize consultas com a agenda do Google.",
      icon: CalendarClock,
      status: "breve",
      tone: "from-chart-2/15 to-primary/15 text-chart-2",
    },
    {
      title: "ERP / Outros",
      description: "Integre com sistemas externos via API.",
      icon: Boxes,
      status: "breve",
      tone: "from-muted to-muted text-muted-foreground",
    },
  ];

  return (
    <AppShell title="Integrações" subtitle="Conecte a OdontoPlug aos seus canais e serviços">
      <div className="mx-auto max-w-5xl p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          {integracoes.map((item) => {
            const cartao = <CartaoIntegracao {...item} />;
            return item.to ? (
              <Link key={item.title} to={item.to} className="rounded-xl focus-visible:ring-2 focus-visible:ring-ring">
                {cartao}
              </Link>
            ) : (
              <div key={item.title} className="cursor-default opacity-80">
                {cartao}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}

const SELOS = {
  conectado: { texto: "Conectado", variant: "success" },
  pendente: { texto: "Configurado · teste pendente", variant: "warning" },
  disponivel: { texto: "Disponível", variant: "secondary" },
  breve: { texto: "Em breve", variant: "outline" },
};

function CartaoIntegracao({ title, description, icon: Icon, tone, status }) {
  const selo = SELOS[status] ?? SELOS.disponivel;

  return (
    <Card className="group flex h-full flex-col gap-3 p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <Badge variant={selo.variant} className="text-[10px]">
          {selo.texto}
        </Badge>
      </div>

      <div className="flex-1">
        <h3 className="font-display text-base font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      {status !== "breve" && (
        <div className="flex items-center gap-1 text-sm font-medium text-primary">
          Configurar
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </div>
      )}
    </Card>
  );
}
